/**
 * Firebase application bootstrap.
 *
 * Uses the idempotent `getApps().length === 0 ? initializeApp() : getApp()`
 * guard so Vite HMR doesn't throw "Firebase: Firebase App named '[DEFAULT]'
 * already exists" during fast-refresh cycles.
 *
 * If VITE_FIREBASE_PROJECT_ID is absent (e.g. CI without a .env file), all
 * three exports are `null` and the hooks in src/core/realtime/ return idle
 * state. The app still mounts — only Firestore features are unavailable.
 */

import { getApps, initializeApp, getApp } from 'firebase/app';
import { getFirestore } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import type { FirebaseApp } from 'firebase/app';
import type { Firestore } from 'firebase/firestore';
import type { Auth } from 'firebase/auth';
import { env } from '@/config/env';

function initFirebase(): {
  firebaseApp: FirebaseApp | null;
  firestore: Firestore | null;
  auth: Auth | null;
} {
  if (!env.firebase.projectId) {
    // Warning already emitted by env.ts at module load; no need to repeat.
    return { firebaseApp: null, firestore: null, auth: null };
  }

  const app =
    getApps().length === 0
      ? initializeApp({
          apiKey: env.firebase.apiKey,
          authDomain: env.firebase.authDomain,
          projectId: env.firebase.projectId,
          storageBucket: env.firebase.storageBucket,
          messagingSenderId: env.firebase.messagingSenderId,
          appId: env.firebase.appId,
        })
      : getApp();

  return {
    firebaseApp: app,
    firestore: getFirestore(app),
    auth: getAuth(app),
  };
}

const { firebaseApp, firestore, auth } = initFirebase();

export { firebaseApp, firestore, auth };
